import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ItemCard from "@/components/portfolio/ItemCard";
import MediaGallery from "@/components/portfolio/MediaGallery";
import { Reveal, ChapterBadge } from "@/components/Reveal";
import { api } from "@/lib/api";
import { useLenis } from "@/lib/scroll";

export default function PortfolioItem() {
  useLenis();
  const { categorySlug, itemSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/portfolio/items/${categorySlug}/${itemSlug}`)
      .then((r) => {
        setData(r.data);
        document.title = `${r.data.item.title} | Sahil Paruthi`;
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [categorySlug, itemSlug]);

  return (
    <div className="min-h-screen bg-parchment text-charcoal-900">
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <p className="text-sm text-charcoal-500 font-mono">Loading...</p>
          ) : notFound ? (
            <div data-testid="item-not-found">
              <h1 className="font-serif text-3xl text-charcoal-900">Work not found</h1>
              <p className="mt-2 text-charcoal-600">This piece may have been unpublished.</p>
              <Link to="/portfolio" className="mt-4 inline-flex items-center gap-2 text-sm text-copper-600">
                <ArrowLeft className="w-4 h-4" /> Back to portfolio
              </Link>
            </div>
          ) : (
            <>
              <Reveal>
                <Link
                  to={`/portfolio/${data.category.slug}`}
                  data-testid="back-to-category"
                  className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-copper-600 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> {data.category.name}
                </Link>
              </Reveal>

              <div className="mt-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
                <Reveal className="lg:col-span-7">
                  <MediaGallery media={data.item.media} title={data.item.title} />
                </Reveal>

                <Reveal delay={0.1} className="lg:col-span-5">
                  <p className="font-mono text-xs tracking-[0.22em] uppercase text-copper-600">
                    {data.category.name}
                    {data.item.subcategory ? ` / ${data.item.subcategory}` : ""}
                  </p>
                  <h1 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight text-charcoal-900 leading-[1.1]">
                    {data.item.title}
                  </h1>
                  {data.item.year && (
                    <p className="mt-2 font-mono text-xs tracking-[0.2em] text-charcoal-500">{data.item.year}</p>
                  )}
                  {data.item.description && (
                    <p className="mt-5 text-base text-charcoal-600 leading-relaxed">{data.item.description}</p>
                  )}

                  <dl className="mt-8 flex flex-col gap-4 border-t border-charcoal-900/10 pt-6">
                    {data.item.client && (
                      <div className="flex gap-4">
                        <dt className="w-24 shrink-0 font-mono text-[11px] tracking-[0.15em] uppercase text-charcoal-500 pt-0.5">Client</dt>
                        <dd className="text-sm text-charcoal-800">{data.item.client}</dd>
                      </div>
                    )}
                    {data.item.tools?.length > 0 && (
                      <div className="flex gap-4">
                        <dt className="w-24 shrink-0 font-mono text-[11px] tracking-[0.15em] uppercase text-charcoal-500 pt-0.5">Tools</dt>
                        <dd className="text-sm text-charcoal-800">{data.item.tools.join(", ")}</dd>
                      </div>
                    )}
                    {data.item.tags?.length > 0 && (
                      <div className="flex gap-4">
                        <dt className="w-24 shrink-0 font-mono text-[11px] tracking-[0.15em] uppercase text-charcoal-500 pt-1">Tags</dt>
                        <dd className="flex flex-wrap gap-1.5">
                          {data.item.tags.map((t) => (
                            <span key={t} className="text-xs text-charcoal-600 bg-surface-muted border border-charcoal-900/5 rounded-full px-2.5 py-1">
                              {t}
                            </span>
                          ))}
                        </dd>
                      </div>
                    )}
                    {data.item.notes && (
                      <div className="flex gap-4">
                        <dt className="w-24 shrink-0 font-mono text-[11px] tracking-[0.15em] uppercase text-charcoal-500 pt-0.5">Notes</dt>
                        <dd className="text-sm text-charcoal-600 leading-relaxed">{data.item.notes}</dd>
                      </div>
                    )}
                  </dl>
                </Reveal>
              </div>

              {data.related.length > 0 && (
                <div className="mt-20" data-testid="related-work">
                  <div className="flex items-center gap-3 mb-8">
                    <span className="font-mono text-xs tracking-[0.25em] uppercase text-charcoal-500">
                      More from {data.category.name}
                    </span>
                    <span className="h-px flex-1 bg-charcoal-900/10" />
                    <Link
                      to="/portfolio"
                      data-testid="explore-all-work"
                      className="font-mono text-xs tracking-[0.2em] uppercase text-copper-600 hover:text-copper-500"
                    >
                      Explore all work
                    </Link>
                  </div>
                  <div className="columns-1 sm:columns-2 lg:columns-4 gap-6">
                    {data.related.map((item) => (
                      <ItemCard key={item.id} item={item} categorySlug={data.category.slug} categoryName={data.category.name} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
